
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface WaiverAgreement {
  id: string;
  user_id: string;
  provider_id: string;
  waiver_id: string;
  agreed_at: string;
  created_at: string;
}

export const useUserWaiverAgreements = (providerId?: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["user-waiver-agreements", user?.id, providerId],
    queryFn: async () => {
      if (!user?.id || !providerId) {
        return [];
      }

      console.log("Fetching user waiver agreements for provider:", providerId);
      
      const { data: agreements, error } = await supabase
        .from('user_waiver_agreements')
        .select('*')
        .eq('user_id', user.id)
        .eq('provider_id', providerId);

      if (error) {
        console.error('Error fetching waiver agreements:', error);
        throw error;
      }

      console.log("User waiver agreements fetched:", agreements);
      return agreements as WaiverAgreement[];
    },
    enabled: !!user?.id && !!providerId,
  });
};

export const useCreateWaiverAgreement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ providerId, waiverId }: { providerId: string; waiverId: string }) => {
      if (!user?.id) {
        throw new Error("User must be authenticated");
      }

      console.log("Creating waiver agreement:", { providerId, waiverId, userId: user.id });

      const { data: agreement, error } = await supabase
        .from('user_waiver_agreements')
        .insert({
          user_id: user.id,
          provider_id: providerId,
          waiver_id: waiverId
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating waiver agreement:', error);
        throw error;
      }

      console.log("Waiver agreement created:", agreement);
      return agreement as WaiverAgreement;
    },
    onSuccess: (data) => {
      // Invalidate and refetch waiver agreements
      queryClient.invalidateQueries({
        queryKey: ["user-waiver-agreements", user?.id, data.provider_id]
      });
    },
  });
};
